/* ═══════════════════════════════════════════════════════════════
   DARF — Matcher (Hero MER portion calculator)
   RER = 70 × weight_kg^0.75
   MER = RER × life_stage_factor × activity_mod
   grams/day = MER × 1000 / kcal_per_kg
   ═══════════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  const root = document.querySelector('[data-darf-matcher]');
  if (!root) return;

  // ── Config from section settings ─────────────────────────────
  const cfg = {
    barfKcal:  parseFloat(root.dataset.barfKcal)  || 1650,
    coldKcal:  parseFloat(root.dataset.coldKcal)  || 3700,
    barfPack:  parseFloat(root.dataset.barfPack)  || 500,
    coldPack:  parseFloat(root.dataset.coldPack)  || 4000,
    urls: {
      dog_barf: root.dataset.urlDogBarf  || '/collections/hund-barf',
      dog_cold: root.dataset.urlDogCold  || '/collections/hund-kaltgepresst',
      cat_barf: root.dataset.urlCatBarf  || '/collections/katze-barf',
      cat_cold: root.dataset.urlCatCold  || '/collections/katze-kaltgepresst',
    }
  };

  // ── State ────────────────────────────────────────────────────
  const state = {
    pet: 'dog',        // dog | cat
    style: 'barf',     // barf | cold | unsure
    name: '',
    weight: null,
    stage: 'adult',    // puppy_young | puppy | adult | senior
    activity: 'normal' // calm | normal | active
  };

  // ── MER factors (Waltham / NRC-style) ────────────────────────
  const BASE = {
    dog: { puppy_young: 3.0, puppy: 2.0, adult: 1.6, senior: 1.4 },
    cat: { puppy_young: 2.5, puppy: 2.0, adult: 1.2, senior: 1.1 }
  };
  // Activity multipliers (only applied on adults)
  const ACT = {
    dog: { calm: 0.875, normal: 1.0, active: 1.25 },
    cat: { calm: 0.92,  normal: 1.0, active: 1.17 }
  };

  // ── DOM refs ─────────────────────────────────────────────────
  const $  = (sel) => root.querySelector(sel);
  const $$ = (sel) => root.querySelectorAll(sel);

  const els = {
    stepInput:  $('[data-step="input"]'),
    stepResult: $('[data-step="result"]'),
    submit:     $('[data-submit]'),
    back:       $('[data-back]'),
    name:       $('[data-field="name"]'),
    weight:     $('[data-field="weight"]'),
    stage:      $('[data-field="stage"]'),
    unsureHint: $('[data-unsure-hint]'),
    resName:    $('[data-result-name]'),
    resKcal:    $('[data-result-kcal]'),
    resGrams:   $('[data-result-grams]'),
    resPacks:   $('[data-result-packs]'),
    resRec:     $('[data-result-rec]'),
    resCta:     $('[data-result-cta]'),
    save:       $('[data-save]'),
    saveLabel:  $('[data-save-label]'),
    bowlFill:   root.querySelector('.matcher__bowl-fill'),
  };

  // ── Chip group handlers ──────────────────────────────────────
  $$('.matcher__chip[data-group]').forEach(btn => {
    btn.addEventListener('click', () => {
      const group = btn.dataset.group;
      const val   = btn.dataset.value;
      $$(`.matcher__chip[data-group="${group}"]`).forEach(c => {
        c.classList.remove('is-active');
        c.setAttribute('aria-checked', 'false');
      });
      btn.classList.add('is-active');
      btn.setAttribute('aria-checked', 'true');
      state[group] = val;

      if (group === 'style') {
        els.unsureHint.hidden = (val !== 'unsure');
      }
      updateSubmitState();
    });
  });

  // ── Input handlers ───────────────────────────────────────────
  els.name.addEventListener('input', () => {
    state.name = els.name.value.trim();
    updateSubmitState();
  });
  els.weight.addEventListener('input', () => {
    const n = parseFloat(els.weight.value);
    state.weight = (!isNaN(n) && n > 0 && n <= 100) ? n : null;
    updateSubmitState();
  });
  els.stage.addEventListener('change', () => {
    state.stage = els.stage.value;
  });

  function updateSubmitState() {
    const ok = state.name.length > 0 && state.weight !== null;
    els.submit.disabled = !ok;
  }
  updateSubmitState();

  // ── Submit & calculation ─────────────────────────────────────
  els.submit.addEventListener('click', () => {
    if (els.submit.disabled) return;
    const result = calculate(state);
    renderResult(result);
    showStep('result');
  });
  els.back.addEventListener('click', () => showStep('input'));

  function calculate(s) {
    const rer = 70 * Math.pow(s.weight, 0.75);
    const baseFactor = BASE[s.pet][s.stage];
    const actMod = (s.stage === 'adult') ? ACT[s.pet][s.activity] : 1.0;
    const factor = baseFactor * actMod;
    const mer = rer * factor;

    const effStyle = (s.style === 'unsure') ? 'cold' : s.style;
    const kcalPerKg = (effStyle === 'barf') ? cfg.barfKcal : cfg.coldKcal;
    const packG     = (effStyle === 'barf') ? cfg.barfPack : cfg.coldPack;

    const grams  = mer * 1000 / kcalPerKg;
    const packs  = Math.ceil((grams * 30) / packG);

    const urlKey = `${s.pet}_${effStyle}`;
    const url    = cfg.urls[urlKey];

    const styleLabelMap = { barf: 'BARF', cold: 'Cold-pressed' };
    const petLabelMap = {
      dog: { en: 'Dog', de: 'Hund' },
      cat: { en: 'Cat', de: 'Katze' }
    };
    const iso = (document.documentElement.lang || 'en').slice(0, 2);
    const petLabel = petLabelMap[s.pet][iso] || petLabelMap[s.pet].en;

    return {
      ...s,
      effStyle,
      rer:   Math.round(rer),
      mer:   Math.round(mer),
      grams: Math.round(grams),
      packs,
      url,
      rec: `DARF ${styleLabelMap[effStyle]} — ${petLabel}`,
      packSize: packG,
    };
  }

  // ── Render (safe: textContent + createElement, no innerHTML) ──
  function setStatValue(el, value, suffix) {
    if (!el) return;
    // Clear existing children safely.
    while (el.firstChild) el.removeChild(el.firstChild);
    el.appendChild(document.createTextNode(String(value)));
    if (suffix) {
      const em = document.createElement('em');
      em.textContent = suffix;
      el.appendChild(em);
    }
  }

  function renderResult(r) {
    els.resName.textContent = r.name;
    animateNumber(els.resKcal, r.mer, '', 900);
    animateNumber(els.resGrams, r.grams, 'g', 900);
    animateNumber(els.resPacks, r.packs, '', 700);
    els.resRec.textContent = r.rec;
    els.resCta.setAttribute('href', r.url);

    // Bowl fill: reference ratio. BARF 400g ≈ 70% ; Cold 200g ≈ 70%. Clamp 15-95%.
    const refG = (r.effStyle === 'barf') ? 400 : 200;
    const pct  = Math.max(15, Math.min(95, (r.grams / refG) * 70));
    requestAnimationFrame(() => {
      if (els.bowlFill) {
        els.bowlFill.setAttribute('y', String(130 - (130 * pct / 100)));
        els.bowlFill.setAttribute('height', String(130 * pct / 100));
      }
    });

    try {
      localStorage.setItem('darf:matcher:lastPlan', JSON.stringify(r));
    } catch (e) { /* noop */ }
  }

  function animateNumber(el, target, suffix, duration) {
    if (!el) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setStatValue(el, target, suffix);
      return;
    }
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const val = Math.round(target * eased);
      setStatValue(el, val, suffix);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  function showStep(name) {
    const isInput = name === 'input';
    els.stepInput.hidden  = !isInput;
    els.stepResult.hidden =  isInput;
  }

  // ── Save plan (localStorage for MVP; future: Hundeprofil API) ──
  if (els.save && els.saveLabel) {
    const originalText = els.saveLabel.textContent;
    els.save.addEventListener('click', () => {
      els.saveLabel.textContent = '✓';
      els.save.classList.add('is-saved');
      setTimeout(() => {
        els.saveLabel.textContent = originalText;
        els.save.classList.remove('is-saved');
      }, 1800);
    });
  }

  // ── Restore last plan on load (pre-fill only, don't jump to result) ──
  try {
    const saved = localStorage.getItem('darf:matcher:lastPlan');
    if (saved) {
      const p = JSON.parse(saved);
      if (p.name)   { els.name.value = p.name;     state.name = p.name; }
      if (p.weight) { els.weight.value = p.weight; state.weight = p.weight; }
      if (p.stage && els.stage.querySelector(`option[value="${p.stage}"]`)) {
        els.stage.value = p.stage; state.stage = p.stage;
      }
      ['pet', 'style', 'activity'].forEach(g => {
        if (!p[g]) return;
        const chip = root.querySelector(`.matcher__chip[data-group="${g}"][data-value="${p[g]}"]`);
        if (!chip) return;
        $$(`.matcher__chip[data-group="${g}"]`).forEach(c => {
          c.classList.remove('is-active');
          c.setAttribute('aria-checked', 'false');
        });
        chip.classList.add('is-active');
        chip.setAttribute('aria-checked', 'true');
        state[g] = p[g];
      });
      updateSubmitState();
    }
  } catch (e) { /* noop */ }
})();
